const UOBConfigLinePatternRe = /(?<key>[^:]*)?:(?<name>[^:]*)?(:\((?<params>.*)\))?$/gm
const UOBConfigLineParamPatterRe = /(?<key>[a-zA-Z][a-zA-Z0-9]*)\s*:\s*(?<value>[^|]+)/g
const UOBConfigOptionTypePatternReMap = {
  text: { pattern: /^text$/ },
  select: {
    pattern: /select\((.+)?\)/,
    fn: (value) => {
      const items = []
      for (const item of value.matchAll(/((?<key>[a-zA-Z0-9]+)?:(?<name>[^,)]+))/g)) {
        items.push({
          key: item.groups.key,
          name: item.groups.name
        })
      }
      return {
        type: 'select',
        items
      }
    }
  }
}

const UOBParser = {
  cleanLineBreak (text) {
    const parsed1 = text.replace(/\n|\r/gm, '\n').split('\n')

    let lines = []
    let currentLine = ''
    let currentOpenParen = 0
    let currentCloseParen = 0
    for (const line of parsed1) {
      currentOpenParen += (line.match(/\(/g) || []).length
      currentCloseParen += (line.match(/\)/g) || []).length
      currentLine += line
      if (currentOpenParen === currentCloseParen) {
        lines.push(currentLine)
        currentLine = ''
        currentOpenParen = 0
        currentCloseParen = 0
      }
    }

    return lines
  },
  parsePlaceOptionParams (text) {
    if (!text) {
      return null
    }
    const matched = text.matchAll(UOBConfigLineParamPatterRe)
    if (!matched) {
      return null
    }
    const params = {}
    for (const match of matched) {
      const key = match.groups.key
      const value = match.groups.value

      let newValue = value

      switch (key) {
        case 'optionType':
          const matchedKey = Object.keys(UOBConfigOptionTypePatternReMap).find((key) => {
            const pattern = UOBConfigOptionTypePatternReMap[key]
            if (pattern) {
              return pattern.pattern.test(value)
            }
          })
          if (matchedKey && UOBConfigOptionTypePatternReMap[matchedKey].fn) {
            newValue = UOBConfigOptionTypePatternReMap[matchedKey].fn(value)
          } else if (matchedKey) {
            newValue = { type: matchedKey }
          }
          break
      }

      params[key] = newValue
    }
    return params
  },
  parsePlaceOption (text) {
    const lines = this.cleanLineBreak(text)

    const matched = lines
      .join('\n')
      .matchAll(UOBConfigLinePatternRe)
    const conf = []
    for (const match of matched) {
      conf.push({
        key: match.groups.key ? match.groups.key.trim() || null : null,
        name: match.groups.name.trim(),
        params: this.parsePlaceOptionParams(match.groups.params)
      })
    }

    return conf
  }
}

const LinkParser = {
  parse (text) {
    const linkRegex = /\[(?<text>[^\]]+)]\((?<url>[^)]+)\)/g
    const links = []
    let match
    while ((match = linkRegex.exec(text)) !== null) {
      const linkData = this.parseUrl(match.groups.url)
      links.push({
        text: match.groups.text,
        url: linkData.url,
        target: linkData.target,
        class: linkData.class,
      })
    }
    return links
  },

  parseImg (text) {
    const imgRegex = /!\[(?<alt>[^\]]*?)]\((?<src>[^)]+)\)/g
    const images = []
    let match
    while ((match = imgRegex.exec(text)) !== null) {
      const imgData = this.parseImgSrc(match.groups.src)
      images.push({
        alt: match.groups.alt,
        src: imgData.src,
        class: imgData.class,
        width: imgData.width,
        height: imgData.height,
      })
    }
    return images
  },

  parseImgSrc (srcString) {
    // Check if it's a simple src (standard markdown)
    if (!srcString.includes('|') && !srcString.replace(/^http(s)?:\/\//, '').includes(':')) {
      return { src: srcString }
    }

    // Check if it's the extended format with options
    const result = { src: '', class: null, width: null, height: null, attributes: {} }

    // Split by | to get individual options
    const parts = srcString.split('|')

    for (const part of parts) {
      const trimmedPart = part.trim()

      if (trimmedPart.startsWith('src:')) {
        result.src = trimmedPart.substring(4)
      } else if (trimmedPart.startsWith('class:')) {
        result.class = trimmedPart.substring(6)
      } else if (trimmedPart.startsWith('width:')) {
        result.width = trimmedPart.substring(6)
      } else if (trimmedPart.startsWith('height:')) {
        result.height = trimmedPart.substring(7)
      } else if (trimmedPart.includes(':')) {
        // Handle other custom attributes
        const [key, value] = trimmedPart.split(':', 2)
        result.attributes[key] = value
      } else if (!result.src) {
        // If no 'src:' prefix found, treat first part as src
        result.src = trimmedPart
      }
    }

    return result
  },

  parseLinkedImg (text) {
    // Pattern: [![alt text](image-src)](link-url)
    const linkedImgRegex = /\[!\[(?<alt>[^\]]*?)\]\((?<src>[^)]+)\)\]\((?<url>[^)]+)\)/g
    const linkedImages = []
    let match
    while ((match = linkedImgRegex.exec(text)) !== null) {
      const imgData = this.parseImgSrc(match.groups.src)
      const linkData = this.parseUrl(match.groups.url)
      linkedImages.push({
        type: 'linked-image',
        image: {
          alt: match.groups.alt,
          src: imgData.src,
          class: imgData.class,
          width: imgData.width,
          height: imgData.height,
        },
        link: {
          url: linkData.url,
          target: linkData.target,
          class: linkData.class,
        }
      })
    }
    return linkedImages
  },

  parseAll (text) {
    const results = []

    // First, find all linked images and mark their positions
    const linkedImgRegex = /\[!\[(?<alt>[^\]]*?)\]\((?<src>[^)]+)\)\]\((?<url>[^)]+)\)/g
    const linkedImgMatches = []
    let match

    while ((match = linkedImgRegex.exec(text)) !== null) {
      linkedImgMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        match: match
      })
    }

    // Find all standalone images (not part of linked images)
    const imgRegex = /!\[(?<alt>[^\]]*?)]\((?<src>[^)]+)\)/g
    while ((match = imgRegex.exec(text)) !== null) {
      // Check if this image is part of a linked image
      const isPartOfLinkedImg = linkedImgMatches.some(linkedImg =>
        match.index >= linkedImg.start && match.index < linkedImg.end
      )

      if (!isPartOfLinkedImg) {
        const imgData = this.parseImgSrc(match.groups.src)
        results.push({
          type: 'image',
          position: match.index,
          content: {
            alt: match.groups.alt,
            src: imgData.src,
            class: imgData.class,
            width: imgData.width,
            height: imgData.height,
          }
        })
      }
    }

    // Find all regular links (not linked images)
    const linkRegex = /\[(?<text>[^\]]+)]\((?<url>[^)]+)\)/g
    while ((match = linkRegex.exec(text)) !== null) {
      // Check if this is a linked image
      const isLinkedImg = match.groups.text.startsWith('![')

      if (!isLinkedImg) {
        const linkData = this.parseUrl(match.groups.url)
        results.push({
          type: 'link',
          position: match.index,
          content: {
            text: match.groups.text,
            url: linkData.url,
            target: linkData.target,
            class: linkData.class,
          }
        })
      }
    }

    // Add all linked images
    console.log(linkedImgMatches)
    linkedImgMatches.forEach(linkedImg => {
      const match = linkedImg.match
      const imgData = this.parseImgSrc(match.groups.src)
      const linkData = this.parseUrl(match.groups.url)
      console.log(`
        src: ${match.groups.src}
        url: ${match.groups.url}
        imgData: ${imgData.src}
      `)
      results.push({
        type: 'linked-image',
        position: match.index,
        content: {
          image: {
            alt: match.groups.alt,
            src: imgData.src,
            class: imgData.class,
            width: imgData.width,
            height: imgData.height,
          },
          link: {
            url: linkData.url,
            target: linkData.target,
            class: linkData.class,
          }
        }
      })
    })

    // Sort by position to maintain order
    results.sort((a, b) => a.position - b.position)

    return results
  },

  parseUrl (urlString) {
    // Check if it's a simple URL (standard markdown)
    if (!urlString.includes('|') && !urlString.includes(':')) {
      return { url: urlString }
    }

    // Check if it's the extended format with options
    const result = { url: '', target: null, class: null, attributes: {} }

    // Split by | to get individual options
    const parts = urlString.split('|')

    for (const part of parts) {
      const trimmedPart = part.trim()

      if (trimmedPart.startsWith('to:')) {
        result.url = trimmedPart.substring(3)
      } else if (trimmedPart.startsWith('target:')) {
        result.target = trimmedPart.substring(7)
      } else if (trimmedPart.startsWith('class:')) {
        result.class = trimmedPart.substring(6)
      } else if (trimmedPart.includes(':')) {
        // Handle other custom attributes
        const [key, value] = trimmedPart.split(':', 2)
        result.attributes[key] = value
      } else if (!result.url) {
        // If no 'to:' prefix found, treat first part as URL
        result.url = trimmedPart
      }
    }

    return result
  }
}

export default {
  uob: UOBParser,
  link: LinkParser,
}



// WEBPACK FOOTER //
// ./src/parser.js